import React, { useState, useEffect, useCallback } from 'react';
import { leaveSlotService, LeaveSlot, LeaveSlotUpdate } from '../../../services/availability/leaveSlotService';
import { DoctorPractice } from '../../../services/profile/profileService';
import '../AddLeaveForm/AddLeaveForm.css';

interface EditLeaveFormProps {
  leave: LeaveSlot;
  practices: DoctorPractice[];
  onClose: () => void;
  onSuccess: () => void;
}

type LeaveType = 'holiday' | 'sick_leave' | 'emergency' | 'other';
type LeaveScope = 'all_practices' | 'single_practice';

const EditLeaveForm: React.FC<EditLeaveFormProps> = ({
  leave,
  practices,
  onClose,
  onSuccess,
}) => {
  const [leaveDate, setLeaveDate] = useState<Date>(() => {
    return new Date(leave.leave_date);
  });
  const [startTime, setStartTime] = useState<string>(leave.start_time);
  const [endTime, setEndTime] = useState<string>(leave.end_time);
  const [selectedLeaveType, setSelectedLeaveType] = useState<LeaveType>(
    leave.leave_type as LeaveType
  );
  const [reason, setReason] = useState<string>(leave.reason || '');
  const [leaveScope, setLeaveScope] = useState<LeaveScope>(
    leave.leave_scope as LeaveScope
  );
  const [timeValidationError, setTimeValidationError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const validateTimeRange = useCallback(() => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    if (endMinutes <= startMinutes) {
      setTimeValidationError('End time must be after start time');
    } else {
      setTimeValidationError(null);
    }
  }, [startTime, endTime]);

  useEffect(() => {
    validateTimeRange();
  }, [validateTimeRange]);

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartTime(e.target.value);
    validateTimeRange();
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndTime(e.target.value);
    validateTimeRange();
  };

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isFormValid = (): boolean => {
    if (timeValidationError) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (leaveDate < today) {
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      const update: LeaveSlotUpdate = {
        leave_date: formatDateString(leaveDate),
        start_time: startTime,
        end_time: endTime,
        leave_type: selectedLeaveType,
        reason: reason || null,
        leave_scope: leaveScope,
      };

      await leaveSlotService.updateLeaveSlot(leave.id, update, false);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update leave slot');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="add-leave-form">
      <div className="add-leave-form-header">
        <h2>Edit Leave</h2>
        <button type="button" className="add-leave-form-close" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="add-leave-form-body">
        {error && <div className="add-leave-form-error">{error}</div>}

        {/* Date Selection */}
        <div className="add-leave-form-section">
          <label className="add-leave-form-label">Date</label>
          <input
            type="date"
            value={formatDateString(leaveDate)}
            min={formatDateString(new Date())}
            onChange={(e) => setLeaveDate(new Date(e.target.value))}
            className="add-leave-form-input"
          />
        </div>

        {/* Time Selection */}
        <div className="add-leave-form-section">
          <label className="add-leave-form-label">Time</label>
          <input
            type="time"
            value={startTime}
            onChange={handleStartTimeChange}
            className="add-leave-form-input"
          />
          <input
            type="time"
            value={endTime}
            onChange={handleEndTimeChange}
            className="add-leave-form-input"
          />
          {timeValidationError && (
            <div className="add-leave-form-error-text">{timeValidationError}</div>
          )}
        </div>

        {/* Leave Type */}
        <div className="add-leave-form-section">
          <label className="add-leave-form-label">Leave Type</label>
          <select
            value={selectedLeaveType}
            onChange={(e) => setSelectedLeaveType(e.target.value as LeaveType)}
            className="add-leave-form-select"
          >
            <option value="holiday">Holiday</option>
            <option value="sick_leave">Sick Leave</option>
            <option value="emergency">Emergency</option>
            <option value="other">Other</option>
          </select>
          <input
            type="text"
            placeholder="Reason (Optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="add-leave-form-input"
          />
        </div>

        {/* Scope */}
        <div className="add-leave-form-section">
          <label className="add-leave-form-label">Scope</label>
          <select
            value={leaveScope}
            onChange={(e) => setLeaveScope(e.target.value as LeaveScope)}
            className="add-leave-form-select"
          >
            <option value="all_practices">All Practices</option>
            <option value="single_practice">Single Practice</option>
          </select>
        </div>
      </div>

      <div className="add-leave-form-footer">
        <button type="button" className="add-leave-form-cancel" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="add-leave-form-save"
          onClick={handleSubmit}
          disabled={isUpdating || !isFormValid()}
        >
          {isUpdating ? 'Updating...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default EditLeaveForm;

